<template>
  <div id="demo-controls" class="demo-controls no-select" v-if="screenerData">
    <div :class="{ visible: isVisible, hidden: !isVisible }" class="control-panel">
      <div class="control-buttons">
        <MButton
          @click="handlePauseResume"
          class="control-button"
          :disabled="!canControlScreener"
          :useDefaultStyles="false"
          :icon="pausedState ? faPlay : faPause"
          iconPosition="center"
          v-tooltip.bottom.danger="
            canPauseScreener ? 'Pausing now will reset Intro/Outro animations' : null
          "
        />

        <MButton
          @click="handleAction(dataStore.moveToPreviousSubtest)"
          class="control-button"
          :disabled="!dataStore.canMoveToPreviousSubtest || !canControlScreener"
          v-tooltip:800.bottom="'Previous Subtest'"
          :useDefaultStyles="false"
          :icon="faBackward"
          iconPosition="center"
        />
        <MButton
          @click="handleAction(dataStore.moveToNextSubtest)"
          class="control-button"
          :disabled="!dataStore.canMoveToNextSubtest || !canControlScreener"
          v-tooltip:800.bottom="'Next Subtest'"
          :useDefaultStyles="false"
          :icon="faForward"
          iconPosition="center"
        />
        <!-- //TODO: COME BACK TO THIS AND FIX BUGS SWITCHING BETWEEN QUESTIONS
        <MButton
          @click.stop="handleAction(dataStore.skipQuestion)"
          @touchend.stop="handleAction(dataStore.skipQuestion)"
          class="control-button"
          :disabled="!dataStore.canSkipQuestion || !canControlScreener"
        >
          <font-awesome-icon icon="step-forward" />
        </button>
        -->
        <!--
        <MButton
          @click.stop="handleAction(dataStore.skipStep)"
          @touchend.stop="handleAction(dataStore.skipStep)"
          class="control-button"
          :disabled="!canControlScreener"
        >
          <font-awesome-icon icon="step-forward" />
        </button>-->
        <MButton
          @click="toggleSettings"
          class="control-button"
          v-tooltip:800.bottom="'Settings'"
          :useDefaultStyles="false"
          :icon="faCog"
          iconPosition="center"
        />
        <MButton
          @click="toggleDemoControls"
          class="toggle-button"
          :class="{ hidden: !isVisible }"
          :useDefaultStyles="false"
          :icon="isVisible ? faChevronUp : faChevronDown"
          iconPosition="center"
        />
      </div>
      <div class="settings-dropdown">
        <div v-if="showSettings" class="settings-menu">
          <h3>Skip to events:</h3>
          <MButton
            @click="handleAction(dataStore.playScreenerIntro)"
            class="menu-button"
            :disabled="!canControlScreener"
            :useDefaultStyles="false"
            title="Play Screener Intro"
          />
          <MButton
            @click="handleAction(dataStore.playScreenerOutro)"
            class="menu-button"
            :disabled="!canControlScreener"
            :useDefaultStyles="false"
            title="Play Screener Outro"
          />
          <MButton
            @click="handleAction(dataStore.playSubtestReward)"
            class="menu-button"
            :useDefaultStyles="false"
            :disabled="!canControlScreener"
            title="Skip to Last Question in Subtest"
          />
          <h3>Teacher Widget</h3>
          <MButton
            @click="toggleSimulateError"
            class="menu-button"
            :useDefaultStyles="false"
            :title="`${simulateError ? 'Remove' : 'Simulate'} Error on teacher widget`"
          />
          <h3>Simulate Warnings</h3>
          <MButton
            @click="toggleSimulateDeepgramWarning"
            class="menu-button"
            :useDefaultStyles="false"
            :title="`${
              simulateDeepgramWarning ? 'Remove' : 'Simulate'
            } Deepgram Unavailable Warning`"
          />
          <MButton
            @click="toggleSimulateMicWarning"
            class="menu-button"
            :useDefaultStyles="false"
            :title="`${simulateMicWarning ? 'Remove' : 'Simulate'} Incorrect Mic In Use Warning`"
          />
          <div v-if="devStore.isLocalDevSession">
            <h3>Screener Overrides:</h3>
            <label class="checkbox-label">
              <input
                type="checkbox"
                v-model="skipSubtestIntro"
                @change.stop="handleCheckboxChange"
              />
              <span class="checkbox-text">Skip Subtest intros</span>
            </label>
            <label class="checkbox-label">
              <input
                type="checkbox"
                v-model="disableStarPopup"
                @change.stop="handleCheckboxChange"
              />
              <span class="checkbox-text">Disable Star Popup</span>
            </label>
            <label class="checkbox-label" v-if="!devStore.isDemoSession">
              <input
                type="checkbox"
                v-model="sendAnswerToApi"
                @change.stop="handleCheckboxChange"
              />
              <span class="checkbox-text">Send Data to API</span>
            </label>
            <label class="checkbox-label">
              <input
                type="checkbox"
                v-model="skipScreenerIntro"
                @change.stop="handleCheckboxChange"
              />
              <span class="checkbox-text">Skip screener intro</span>
            </label>
            <label class="checkbox-label">
              <input
                type="checkbox"
                v-model="showProgressiveMapPath"
                @change.stop="handleCheckboxChange"
              />
              <span class="checkbox-text">Show progressive map path</span>
            </label>
            <label class="checkbox-label">
              <input
                type="checkbox"
                v-model="forceFullscreen"
                @change.stop="handleCheckboxChange"
              />
              <span class="checkbox-text">Force Fullscreen</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted, nextTick } from 'vue'
import { storeToRefs } from 'pinia'

import MButton from '@/components/MButton.vue'

import { library } from '@fortawesome/fontawesome-svg-core'
import {
  faPlay,
  faPause,
  faStepForward,
  faForward,
  faBackward,
  faChevronUp,
  faChevronDown,
  faCog
} from '@fortawesome/free-solid-svg-icons'

import { useDataStore } from '@/stores/DataStore'
import { useActiveScreenerStore } from '@/stores/activeScreenerStore'
import { useDevStore } from '@/stores/DevStore'
import Logger from '@/services/Logger.js'

const logger: any = new Logger('Demo Controls', true)

library.add(
  faPlay,
  faPause,
  faStepForward,
  faForward,
  faBackward,
  faChevronUp,
  faChevronDown,
  faCog
)

const dataStore = useDataStore()
const activeScreenerStore = useActiveScreenerStore()
const devStore = useDevStore()

const { pausedState, screenerData } = storeToRefs(dataStore)
const { activeScreener } = storeToRefs(activeScreenerStore)

const isVisible = ref(true)
const showSettings = ref(false)
const simulateError = ref(false)
const simulateDeepgramWarning = ref(false)
const simulateMicWarning = ref(false)

const canControlScreener = computed(
  () =>
    ['ScreenerQuestionsView', 'PausedScreenerView', 'ScreenerOutro', 'ScreenerIntro'].includes(
      activeScreener.value?.section || ''
    ) ||
    (dataStore.screenerCompleted && activeScreener.value?.section === 'StartScreenerView')
)

const canPauseScreener = computed(() =>
  ['ScreenerOutro', 'ScreenerIntro'].includes(activeScreener.value?.section || '')
)

const toggleDemoControls = () => {
  isVisible.value = !isVisible.value
}

const handlePauseResume = async () => {
  try {
    await dataStore.setPausedState(!pausedState.value)
    activeScreenerStore.confirmAction(!pausedState.value ? 'Active' : 'Paused')
    await nextTick()
  } catch (error) {
    logger.error('Error in handlePauseResume:', error)
  }
}

const toggleSettings = () => {
  showSettings.value = !showSettings.value
}

const toggleSimulateError = async () => {
  try {
    simulateError.value = !simulateError.value

    if (simulateError.value) {
      await activeScreenerStore.sendScreenerError({
        type: 'NoAnswersDetected',
        message:
          'This is a simulated error for: No answers detected for the last few questions, lack of answers will result in an inconclusive report.',
        priority: 2
      })
    } else {
      await activeScreenerStore.sendScreenerResolvedError({
        type: 'NoAnswersDetected'
      })
    }
    await nextTick()
  } catch (error) {
    logger.error('Error in toggleSimulateError:', error)
  }
}

const handleAction = async (action: () => Promise<void>) => {
  try {
    if (showSettings.value) {
      showSettings.value = false
    }
    await action()
    await nextTick()
  } catch (error) {
    logger.error('Error in handleAction:', error)
  }
}

const handleCheckboxChange = async () => {
  try {
    await nextTick()
  } catch (error) {
    logger.error('Error in handleCheckboxChange:', error)
  }
}

const disableStarPopup = computed({
  get: () => devStore.disableStarPopup,
  set: async (value) => {
    try {
      await devStore.overrideDevValues({ disableStarPopup: value })
      await nextTick()
    } catch (error) {
      logger.error('Error setting disableStarPopup:', error)
    }
  }
})

const skipScreenerIntro = computed({
  get: () => devStore.skipScreenerIntro,
  set: async (value) => {
    try {
      await devStore.overrideDevValues({ skipScreenerIntro: value })
      await nextTick()
    } catch (error) {
      logger.error('Error setting skipScreenerIntro:', error)
    }
  }
})

const skipSubtestIntro = computed({
  get: () => devStore.skipSubtestIntro,
  set: async (value) => {
    try {
      await devStore.overrideDevValues({ skipSubtestIntro: value })
      await nextTick()
    } catch (error) {
      logger.error('Error setting skipSubtestIntro:', error)
    }
  }
})

const sendAnswerToApi = computed({
  get: () => devStore.sendAnswerToApi,
  set: async (value) => {
    try {
      await devStore.overrideDevValues({ sendAnswerToApi: value })
      await nextTick()
    } catch (error) {
      logger.error('Error setting sendAnswerToApi:', error)
    }
  }
})

const showProgressiveMapPath = computed({
  get: () => devStore.drawProgressiveMapPath,
  set: async (value) => {
    try {
      await devStore.overrideDevValues({ drawProgressiveMapPath: value })
      await nextTick()
    } catch (error) {
      logger.error('Error setting drawProgressiveMapPath:', error)
    }
  }
})

const forceFullscreen = computed({
  get: () => devStore.forceFullscreen,
  set: async (value) => {
    try {
      await devStore.overrideDevValues({ forceFullscreen: value })
      await nextTick()
    } catch (error) {
      logger.error('Error setting forceFullscreen:', error)
    }
  }
})

// Cleanup function to reset states
const cleanup = () => {
  isVisible.value = true
  showSettings.value = false
  simulateError.value = false
}

const toggleSimulateDeepgramWarning = async () => {
  try {
    simulateDeepgramWarning.value = !simulateDeepgramWarning.value
    if (simulateDeepgramWarning.value) {
      await activeScreenerStore.sendScreenerWarning({
        type: 'DeepgramUnavailable',
        message:
          'Live transcription is unavailable. This can lead to more pausing issues and failure to acknowledge a pupils responses. Please look in the screener guide to make sure the firewall is setup for Deepgram.',
        priority: 1,
        metadata: {
          reason: 'Simulated reason',
          errorDetails: 'Simulated error details'
        }
      })
    } else {
      await activeScreenerStore.sendScreenerResolvedWarning({
        type: 'DeepgramUnavailable'
      })
    }
    await nextTick()
  } catch (error) {
    logger.error('Error in toggleSimulateDeepgramWarning:', error)
  }
}

const toggleSimulateMicWarning = async () => {
  try {
    simulateMicWarning.value = !simulateMicWarning.value
    if (simulateMicWarning.value) {
      await activeScreenerStore.sendScreenerWarning({
        type: 'IncorrectMicInUse',
        message:
          'The recording isn’t using the available recommended headset, this will affect audio quality and may lead to inconclusive reports. Please exit and re-enter the tech test',
        priority: 1,
        metadata: {
          selectedDevice: 'Simulated selected device',
          preferredDevices: ['Simulated preferred device 1', 'Simulated preferred device 2']
        }
      })
    } else {
      await activeScreenerStore.sendScreenerResolvedWarning({
        type: 'IncorrectMicInUse'
      })
    }
    await nextTick()
  } catch (error) {
    logger.error('Error in toggleSimulateMicWarning:', error)
  }
}

onUnmounted(() => {
  cleanup()
})
</script>

<style lang="scss" scoped>
.demo-controls {
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2000;

  .control-panel {
    background-color: #323444;
    color: #fff;
    padding: 0.5rem;
    border-bottom-left-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
    transition: all 0.3s ease;
    display: flex;
    justify-content: space-between;
    align-items: center;

    &.visible {
      transform: translateY(0);
    }

    &.hidden {
      transform: translateY(-100%);
    }

    .control-buttons {
      display: flex;
      align-items: center;
      gap: 1rem;
      touch-action: manipulation;
    }

    .control-button {
      font-size: 1.25rem;
      background: none;
      border: none;
      color: inherit;
      cursor: pointer;

      &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
      }
    }

    .settings-dropdown {
      position: relative;

      .settings-button {
        font-size: 1.25rem;
        background: none;
        border: none;
        color: inherit;
        cursor: pointer;
      }

      .settings-menu {
        position: absolute;
        top: -1.3rem;
        left: calc(100% + 5px);
        background-color: #323444;
        padding: 1rem;
        border-radius: 0.5rem;
        z-index: 60;
        min-width: 250px;
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);

        h3 {
          margin-top: 0.5rem;
          margin-bottom: 0.5rem;
          font-size: 0.9rem;
          color: #a0aec0;
        }

        .menu-button {
          display: block;
          width: 100%;
          text-align: left;
          background-color: #4a5568;
          border: none;
          color: #fff;
          cursor: pointer;
          padding: 0.5rem 1rem;
          margin-bottom: 0.5rem;
          border-radius: 0.25rem;
          transition: background-color 0.2s ease;

          &:hover {
            background-color: #2d3748;
          }

          &:disabled {
            background-color: #a0aec0;
            cursor: not-allowed;
            opacity: 0.7;
          }
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-top: 0.5rem;
          cursor: pointer;

          input[type='checkbox'] {
            appearance: none;
            width: 18px;
            height: 18px;
            border: 2px solid #a0aec0;
            border-radius: 3px;
            outline: none;
            cursor: pointer;
            position: relative;

            &:checked {
              background-color: #4299e1;
              border-color: #4299e1;

              &::after {
                content: '\2714';
                font-size: 14px;
                color: white;
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
              }
            }
          }

          .checkbox-text {
            font-size: 0.9rem;
          }
        }

        .reset-button {
          margin-top: 1rem;
        }
      }
    }
  }

  .toggle-button {
    background-color: #323444;
    color: #fff;
    padding: 0.25rem 1rem;
    border-bottom-left-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
    position: absolute;
    left: 50%;
    top: 95%;
    transform: translateX(-50%);
    height: 2rem;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    &.hidden {
      height: 1.5rem;
    }
  }
}
</style>
